import { useMutation, useQuery } from '@tanstack/react-query'

import { customInstance } from '../../services/api-client'
import type {
  AgentReview,
  LagAccrualConnectorOption,
  LagAccrualDataSourceConfig,
  LagAccrualRun,
} from './types'

const BASE = '/api/lag-accruals'

export const lagAccrualKeys = {
  all: ['lag-accruals'] as const,
  run: () => [...lagAccrualKeys.all, 'run'] as const,
  agentReview: () => [...lagAccrualKeys.all, 'agent-review'] as const,
  dataSource: () => [...lagAccrualKeys.all, 'data-source'] as const,
  connectors: () => [...lagAccrualKeys.all, 'connectors'] as const,
}

export function useLagAccrualRun() {
  return useQuery<LagAccrualRun>({
    queryKey: lagAccrualKeys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export const lagAccrualExportUrls = {
  journal: `${BASE}/exports/journal.csv`,
  reversal: `${BASE}/exports/reversal.csv`,
}

export function useLagAccrualAgentReview() {
  return useMutation<AgentReview, Error, { use_llm?: boolean }>({
    mutationKey: lagAccrualKeys.agentReview(),
    mutationFn: body =>
      customInstance({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { use_llm: body.use_llm ?? false },
      }),
  })
}

export function useLagAccrualDataSourceConfig() {
  return useQuery<LagAccrualDataSourceConfig>({
    queryKey: lagAccrualKeys.dataSource(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 30_000,
  })
}

export function useLagAccrualConnectors() {
  return useQuery<LagAccrualConnectorOption[]>({
    queryKey: lagAccrualKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 30_000,
  })
}

export function useSaveLagAccrualDataSource() {
  return useMutation<
    LagAccrualDataSourceConfig,
    Error,
    { connector_id: string; redshift_schema?: string | null }
  >({
    mutationFn: data =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data,
      }),
  })
}

export function useTestLagAccrualDataSource() {
  return useMutation<
    { status: string; message: string; redshift_connected: boolean },
    Error,
    { connector_id: string; redshift_schema?: string | null }
  >({
    mutationFn: data =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data,
      }),
  })
}
